import React, { PureComponent, PropTypes } from 'react';
import _ from 'underscore';
import { Icon } from 'react-fast-ui';
import cx from 'classnames';
import FormControl from '../../components/inputs/FormControl';
import { numberFromQuery } from '../../util/routerUtil';

const PAGINATION_DEFAULTS = { start: 0, count: 20 };

class Page extends PureComponent {
  static propTypes = {
    active: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    active: false,
    disabled: false
  };

  handleClick(e) {
    const { onClick, disabled } = this.props;
    if (!disabled) {
      onClick(e);
    }
  }

  render() {
    const { active, disabled, children } = this.props;
    const cn = cx(
      'display-inline-block',
      'pd-sm',
      'border-color-blue-gray border-style-solid border-width-1px',
      'text-center',
      {
        'cursor-pointer': !disabled && !active,
        'bg-color-header-blue': active && !disabled,
        'color-white': active,
        'bg-color-gray-background': disabled
      });
    return (
      <div className={cn} style={{ minWidth: 36 }} onClick={(e) => this.handleClick(e)}>{children}</div>
    );
  }
}

const ELLIPSIS_PAGE = <Page active={false} disabled={true}>…</Page>;
const DEFAULT_PAGINATION_CLASSNAME = cx(
  'align-items-center',
  'display-flex',
  'flex-wrap-wrap',
  'flex-direction-row',
  'hidden-print',
  'sm-down-flex-direction-column',
  'pd-sm'
);

export default class QueryPagination extends PureComponent {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    totalCount: PropTypes.number.isRequired,
    maxPagesDisplayed: PropTypes.number,
    sizes: PropTypes.arrayOf(PropTypes.number),
    start: PropTypes.number,
    count: PropTypes.number,
  };

  static defaultProps = {
    ...PAGINATION_DEFAULTS,
    maxPagesDisplayed: 5,
    sizes: [ 5, 10, 20, 50, 100 ]
  };

  static PAGINATION_DEFAULTS = PAGINATION_DEFAULTS;

  static getPageParams(query) {
    const { start: defaultStart, count: defaultCount } = QueryPagination.PAGINATION_DEFAULTS;
    return {
      start: numberFromQuery(query, 'start') || defaultStart,
      count: numberFromQuery(query, 'count') || defaultCount,
    };
  }

  handleChangeCount(selectedCount) {
    const { onChange } = this.props;
    onChange({ count: selectedCount, start: 0 });
  }

  handleChangeStart(pageNumber) {
    const { count, onChange } = this.props;

    if (this.getCurrentPage() === pageNumber) {
      return;
    }

    onChange({ count, start: pageNumber * count });
  }

  getCurrentPage() {
    return (this.props.start / this.props.count);
  }

  getNumberOfPages() {
    const { count, totalCount } = this.props;

    return count > 0 ? Math.max(Math.ceil(totalCount / count), 1) : 1;
  }

  renderPageSelect() {
    const { count, sizes } = this.props;

    const sizesHasPageSize = _.contains(sizes, count);

    return (
      <FormControl>
        <select onChange={({ target: { value } }) => this.handleChangeCount(+value)} value={count}>
          {
            sizes.map((size) => <option key={size} value={size}>{size}</option>)
          }
          {
            sizesHasPageSize ? null : <option key={count} value={count} hidden>{count}</option>
          }
        </select>
      </FormControl>
    );
  }

  renderPageButtons() {
    const { start, maxPagesDisplayed } = this.props;
    const currentPageNumber = this.getCurrentPage();

    const numPages = this.getNumberOfPages();

    // get the first and last page to display
    let firstPage = Math.max(currentPageNumber - Math.floor(maxPagesDisplayed / 2), 0);
    let lastPage = Math.min(firstPage + maxPagesDisplayed, numPages);

    if (lastPage - firstPage < maxPagesDisplayed) {
      firstPage = Math.max(0, lastPage - 7);
    }

    const isFirstPage = start === 0;
    const isLastPage = currentPageNumber === numPages - 1;

    let firstPageButton = null;
    let lastPageButton = null;
    let firstPageEllipsis = null;
    let lastPageEllipsis = null;

    if (firstPage > 0) {
      firstPageButton = <Page disabled={isFirstPage} onClick={() => this.handleChangeStart(0)}>1</Page>;
      if (firstPage > 1) {
        firstPageEllipsis = ELLIPSIS_PAGE;
      }
    }

    if (lastPage < numPages) {
      lastPageButton =
        <Page disabled={isLastPage} onClick={() => this.handleChangeStart(numPages - 1)}>{numPages}</Page>;
      if (lastPage < numPages - 1) {
        lastPageEllipsis = ELLIPSIS_PAGE;
      }
    }

    return (
      <div className="display-inline-block mg-none pd-none">
        <Page key="prev" disabled={isFirstPage} onClick={() => this.handleChangeStart(currentPageNumber - 1)}>
          <Icon name="chevron-left"/>
        </Page>
        {firstPageButton}
        {firstPageEllipsis}
        {
          _.range(firstPage, lastPage).map((no) => (
            <Page key={no} active={no === currentPageNumber} onClick={() => this.handleChangeStart(no)}>{no + 1}</Page>
          ))
        }
        {lastPageEllipsis}
        {lastPageButton}
        <Page key="next" disabled={isLastPage} onClick={() => this.handleChangeStart(currentPageNumber + 1)}>
          <Icon name="chevron-right"/>
        </Page>
      </div>
    );
  }

  render() {
    const { className, style } = this.props;

    const cn = cx(DEFAULT_PAGINATION_CLASSNAME, className);

    return (
      <div className={cn} style={{ paddingTop: 30, paddingBottom: 40, ...style }}>
        <div key="page-buttons" className="flex-grow-1 mg-bottom-sm">{this.renderPageButtons()}</div>
        <div key="page-select">{ this.renderPageSelect() }</div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/common/components/QueryPagination.jsx